import { useEffect } from 'react'

/**
 * Cmd+K (macOS) or Ctrl+K (everywhere else) opens Ask MDT (MYD-621).
 *
 * Not where the key belongs to something else:
 *
 *   - presenting, and the presenter's own window: the keyboard drives slides
 *   - anything marked `data-ask-mdt-hotkey="off"`, such as the deck editor's
 *     canvas, which has its own shortcuts
 *   - a keypress something else already handled (`defaultPrevented`)
 *
 * Otherwise it wins, inputs included, and `preventDefault` stops the browser
 * putting the cursor in its own address bar (Ctrl+K in Chrome and Firefox).
 */

const PRESENTING = /^\/presentations\/[^/]+\/(present|presenter-view)(\/|$)/

export const isHotkeyRoute = (pathname: string) => !PRESENTING.test(pathname)

/** Is this keydown the Ask MDT shortcut, here? Pure, for tests. */
export const isAskMdtHotkey = (event: KeyboardEvent, pathname: string): boolean => {
	if (event.defaultPrevented || event.repeat) return false
	if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return false
	if (event.key.toLowerCase() !== 'k') return false
	if (!isHotkeyRoute(pathname)) return false
	const target = event.target as Element | null
	return !target?.closest?.('[data-ask-mdt-hotkey="off"]')
}

/**
 * Listen for the shortcut while `enabled`.
 *
 * @param onHotkey  called when it fires
 * @param pathname  the current route, from the router
 */
export const useAskMdtHotkey = (onHotkey: () => void, pathname: string, enabled = true) => {
	useEffect(() => {
		if (!enabled) return undefined
		const listener = (event: KeyboardEvent) => {
			if (!isAskMdtHotkey(event, pathname)) return
			event.preventDefault()
			onHotkey()
		}
		window.addEventListener('keydown', listener)
		return () => window.removeEventListener('keydown', listener)
	}, [onHotkey, pathname, enabled])
}

export default useAskMdtHotkey
